/**
 * useConfirm — reemplaza window.confirm con un modal de la app.
 * React 18 compatible — usa createRoot en vez de ReactDOM.render.
 * NO necesita {ConfirmModal} en el JSX.
 *
 * Uso:
 *   const { confirmar } = useConfirm();
 *   const ok = await confirmar({ mensaje: '¿Seguro?', textoOk: 'Sí', peligro: true });
 *   if (!ok) return;
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import React from 'react';

export const useConfirm = () => {
  const [config, setConfig] = useState(null);
  const resolveRef = useRef(null);
  const rootRef = useRef(null);
  const containerRef = useRef(null);

  const confirmar = useCallback(({
    mensaje = '¿Confirmar?',
    detalle,
    textoOk = 'Confirmar',
    textoCancelar = 'Cancelar',
    peligro = false,
  } = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setConfig({ mensaje, detalle, textoOk, textoCancelar, peligro });
    });
  }, []);

  const responder = useCallback((ok) => {
    setConfig(null);
    resolveRef.current?.(ok);
    resolveRef.current = null;
  }, []);

  useEffect(() => {
    if (!config) {
      // Desmontar si existe
      if (rootRef.current) {
        rootRef.current.unmount();
        rootRef.current = null;
      }
      if (containerRef.current) {
        containerRef.current.remove();
        containerRef.current = null;
      }
      return;
    }

    // Crear contenedor si no existe
    if (!containerRef.current) {
      containerRef.current = document.createElement('div');
      containerRef.current.id = 'use-confirm-portal';
      document.body.appendChild(containerRef.current);
    }

    // Crear root si no existe
    if (!rootRef.current) {
      rootRef.current = createRoot(containerRef.current);
    }

    const modal = React.createElement(
      'div',
      {
        style: {
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 99999,
        },
        onClick: () => responder(false),
      },
      React.createElement(
        'div',
        {
          style: {
            background: '#0f172a',
            border: `1px solid ${config.peligro ? '#ef444466' : '#1e293b'}`,
            borderRadius: '14px',
            padding: '24px 28px',
            maxWidth: '380px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          },
          onClick: (e) => e.stopPropagation(),
        },
        React.createElement('div', { style: { fontSize: '2rem', marginBottom: '10px' } },
          config.peligro ? '⚠️' : '💬'
        ),
        React.createElement('p', {
          style: { color: '#e2e8f0', fontSize: '0.95rem', fontWeight: 'bold', margin: '0 0 6px', lineHeight: 1.4 },
        }, config.mensaje),
        config.detalle
          ? React.createElement('p', {
              style: { color: '#64748b', fontSize: '0.82rem', margin: '0 0 20px', lineHeight: 1.5 },
            }, config.detalle)
          : React.createElement('div', { style: { marginBottom: '20px' } }),
        React.createElement('div', { style: { display: 'flex', gap: '10px', justifyContent: 'center' } },
          React.createElement('button', {
            onClick: () => responder(false),
            style: {
              padding: '9px 22px', background: 'transparent',
              border: '1px solid #334155', borderRadius: '8px',
              color: '#94a3b8', cursor: 'pointer', fontSize: '0.88rem',
            },
          }, config.textoCancelar),
          React.createElement('button', {
            onClick: () => responder(true),
            style: {
              padding: '9px 22px',
              background: config.peligro ? '#ef4444' : '#00f2ff',
              border: 'none', borderRadius: '8px',
              color: config.peligro ? '#fff' : '#000',
              fontWeight: 'bold', cursor: 'pointer', fontSize: '0.88rem',
              boxShadow: config.peligro
                ? '0 0 16px rgba(239,68,68,0.3)'
                : '0 0 16px rgba(0,242,255,0.3)',
            },
          }, config.textoOk)
        )
      )
    );

    rootRef.current.render(modal);
  }, [config, responder]);

  // Cleanup al desmontar el componente
  useEffect(() => {
    return () => {
      if (rootRef.current) {
        rootRef.current.unmount();
        rootRef.current = null;
      }
      if (containerRef.current) {
        containerRef.current.remove();
        containerRef.current = null;
      }
    };
  }, []);

  return { confirmar };
};